import React, { useEffect, useState } from 'react';
import ShipToAddressForm from './ShippingAddressForm';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import ShippingAddressInfo from '../../../components/ShippingAddressInfo/ShippingAddressInfo';
import Loader from '../../../components/Common/Loader';
import AssignBooksPreOrder from './AssignBooksPreOrder';
import { BookModel } from '../../../interfaces/book.interface';
import BackButton from '../../../components/Common/BackButton';
import { TeacherClassroomStudent } from '../pages/TeacherClassroomStudents';
import { setStudentPreOrderBook } from '../../../store/userSlice';
import { ShipPreview } from '../services/TeacherShipping.service';

export const ShipToAddressConfirmSteps = {
  AssignBooksPreOrder: -1,
  ShipForm: 0,
  ShipConfirmation: 1,
};

interface ShipToAddressConfirmFlowProps {
  selectedPurchasedBooks: number;
  handleShipSelectedBooks: () => void;
  closeModal: () => void;
  shippingLoader?: boolean;
  studentPreOrders: { user: TeacherClassroomStudent; books: BookModel[] }[];
  // Server-computed list of what this release will actually ship (null while loading).
  preview?: ShipPreview | null;
  // Fail closed: while the preview is loading or failed, confirming is blocked — a missing
  // preview must never mean "nothing is skipped".
  previewStatus?: 'loading' | 'ready' | 'error';
  onRetryPreview?: () => void;
}

const ShipToAddressConfirmFlow: React.FC<ShipToAddressConfirmFlowProps> = ({
  selectedPurchasedBooks,
  handleShipSelectedBooks,
  closeModal,
  shippingLoader,
  studentPreOrders,
  preview,
  previewStatus = 'ready',
  onRetryPreview,
}: ShipToAddressConfirmFlowProps) => {
  const dispatch: AppDispatch = useDispatch();

  const user = useSelector((state: RootState) => state.user.user);
  const studentPreOrderBook = useSelector((state: RootState) => state.user.studentPreOrderBook);
  const allSteps = Object.values(ShipToAddressConfirmSteps);
  const [currentStep, setCurrentStep] = useState<number>(-1);
  const [isEditing, setIsEditing] = useState<number>(-2);
  const [initPreOrderStep, setInitPreOrderStep] = useState<boolean>(false);
  // Explicit opt-in to a partial release: when some books aren't print-ready, the teacher must
  // acknowledge they ship without them (they go out in a follow-up shipment once fixed).
  const [shipWithoutSkipped, setShipWithoutSkipped] = useState<boolean>(false);

  useEffect(() => {
    if (studentPreOrders.length > 0) {
      setInitPreOrderStep(true);
      setCurrentStep(-1);
    } else if (user?.shippingAddresses?.length && user?.shippingAddresses[0]?.shippingAddress1) {
      setCurrentStep(1);
    } else {
      setCurrentStep(0);
    }
  }, []);

  useEffect(() => {
    if (studentPreOrders?.length && !initPreOrderStep) {
      setInitPreOrderStep(true);
      setCurrentStep(-1);
    }
  }, [initPreOrderStep, studentPreOrders]);

  const handleEditAddress = () => {
    setIsEditing(0);
    setCurrentStep(0);
  };

  const handleEditPreOrders = (selectedStudentPreOrder: Record<string, string>) => {
    dispatch(setStudentPreOrderBook(selectedStudentPreOrder));
    setCurrentStep(0);
  };

  const readyOrders = preview?.orders.filter((order) => !order.willSkip) || [];
  const skippedOrders = preview?.orders.filter((order) => order.willSkip) || [];
  const readyPreOrders = preview?.preOrders.filter((preOrder) => !preOrder.willSkip) || [];
  const skippedPreOrders = preview?.preOrders.filter((preOrder) => preOrder.willSkip) || [];
  const skippedTotal = skippedOrders.length + skippedPreOrders.length;
  const nothingReady = !!preview && readyOrders.length + readyPreOrders.length === 0;
  // Partial release requires an explicit opt-in; a full release just needs something to ship.
  const needsSkipAcknowledgement = skippedTotal > 0 && !shipWithoutSkipped;
  // No preview, no confirm: without the server's list we can't know what's skipped, so shipping
  // against the client-derived fallback count would bypass the acknowledgment entirely.
  const previewBlocked = previewStatus !== 'ready';
  const confirmDisabled =
    selectedPurchasedBooks <= 0 || shippingLoader || needsSkipAcknowledgement || previewBlocked || nothingReady;

  // A tick from an earlier preview must not survive into a nothing-ready state: the checkbox
  // unmounts (replaced by the "nothing can ship" note), and a stale true would silently satisfy
  // the acknowledgment if books become ready again later.
  useEffect(() => {
    if (nothingReady && shipWithoutSkipped) {
      setShipWithoutSkipped(false);
    }
  }, [nothingReady, shipWithoutSkipped]);

  return (
    <div className="mx-auto w-full font-manrope">
      {/* Progress bar */}
      <div className={`max-w-[240px] sm:max-w-[280px] mx-auto px-4 pt-2 pb-8`}>
        {/* Static class: a template-built `grid-cols-${n}` never survives the Tailwind purge. */}
        <div className={`grid grid-cols-3 gap-2 sm:gap-4`}>
          {allSteps.map((step, index) => (
            <div
              key={index}
              className={`h-2 ${step <= currentStep ? 'bg-secondaryStoryAdditionalLightBlueText' : 'border'} rounded-full transition-all duration-500 delay-${index * 100}`}
            ></div>
          ))}
        </div>
      </div>
      {currentStep === -1 && (
        <div
          className={`flex ${isEditing === -1 ? 'transform transition-all duration-500 animate-slide-in-left' : ''}`}
        >
          <AssignBooksPreOrder
            studentPreOrders={studentPreOrders}
            studentPreOrderBook={studentPreOrderBook}
            onClickContinue={handleEditPreOrders}
          ></AssignBooksPreOrder>
        </div>
      )}
      {currentStep === 0 && (
        <div
          className={`flex px-6 sm:px-14 lg:px-22 ${isEditing === 0 ? 'transform transition-all duration-500 animate-slide-in-left' : ''}`}
        >
          <ShipToAddressForm
            user={user}
            showEditPreOrderButton={studentPreOrders?.length > 0}
            handleEditPreOrders={() => {
              setIsEditing(-1);
              setCurrentStep(-1);
            }}
            onSaved={() => {
              setIsEditing(1);
              setCurrentStep(1);
            }}
          ></ShipToAddressForm>
        </div>
      )}
      {currentStep === 1 && (
        <div
          className={`flex flex-col ${isEditing === 1 ? 'transform transition-all duration-500 animate-slide-in-right' : ''}`}
        >
          <div className="flex flex-col px-6 sm:px-12 lg:px-18">
            <h1 className="flex justify-center font-bold text-lg sm:text-xl">Send Your Class Shipment</h1>
            <h3 className="flex pl-2 sm:pl-6 text-sm sm:text-base">
              Your classroom gets one Class Shipment — every paid order below is printed together and delivered to
              your school in a single box.
            </h3>
            <div className="flex">
              <ShippingAddressInfo
                userName={user?.firstName}
                numberBookPurchased={selectedPurchasedBooks}
                shippingAddress={user?.shippingAddresses?.length ? user?.shippingAddresses[0] : undefined}
              ></ShippingAddressInfo>
            </div>
            {preview && (readyOrders.length > 0 || readyPreOrders.length > 0) && (
              <div className="flex flex-col pb-2">
                <div className="text-sm font-bold pb-1">
                  Shipping now ({selectedPurchasedBooks} book{selectedPurchasedBooks === 1 ? '' : 's'})
                </div>
                <div className="max-h-32 overflow-y-auto border rounded-lg divide-y">
                  {readyOrders.map((order) => (
                    <div key={order.orderNumber} className="flex justify-between items-center px-3 py-1.5 text-sm">
                      <span className="truncate pr-2">
                        {order.bookTitle || order.bookId}
                        {order.authorName ? ` — ${order.authorName}` : ''}
                      </span>
                      <span className="whitespace-nowrap">×{order.quantity}</span>
                    </div>
                  ))}
                  {readyPreOrders.map((preOrder) => (
                    <div key={preOrder.studentEmail} className="flex justify-between items-center px-3 py-1.5 text-sm">
                      <span className="truncate pr-2">Pre-ordered — {preOrder.studentName || preOrder.studentEmail}</span>
                      <span className="whitespace-nowrap">×{preOrder.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {skippedTotal > 0 && (
              <div className="flex flex-col pb-2">
                <div className="text-sm font-bold text-amber-700 pb-1">
                  Not ready — staying behind ({skippedTotal})
                </div>
                <div className="max-h-28 overflow-y-auto border border-amber-200 bg-amber-50 rounded-lg divide-y divide-amber-100">
                  {skippedOrders.map((order) => (
                    <div key={order.orderNumber} className="flex flex-col px-3 py-1.5 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="truncate pr-2">
                          {order.bookTitle || order.bookId}
                          {order.authorName ? ` — ${order.authorName}` : ''}
                        </span>
                        <span className="whitespace-nowrap">×{order.quantity}</span>
                      </div>
                      {/* Reasons inline, never tooltip-only — tooltips don't exist on touch devices. */}
                      <span className="text-xs text-amber-700">{order.skipReasons.join(', ') || 'Incomplete'}</span>
                    </div>
                  ))}
                  {skippedPreOrders.map((preOrder) => (
                    <div key={preOrder.studentEmail} className="flex flex-col px-3 py-1.5 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="truncate pr-2">
                          Pre-ordered — {preOrder.studentName || preOrder.studentEmail}
                        </span>
                        <span className="whitespace-nowrap">×{preOrder.count}</span>
                      </div>
                      <span className="text-xs text-amber-700">{preOrder.skipReasons.join(', ') || 'Incomplete'}</span>
                    </div>
                  ))}
                </div>
                {readyOrders.length > 0 || readyPreOrders.length > 0 ? (
                  <label className="flex items-start gap-2 pt-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={shipWithoutSkipped}
                      onChange={(e) => setShipWithoutSkipped(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-secondaryStoryAdditionalLightBlueText"
                    />
                    <span>
                      Ship without {skippedTotal === 1 ? 'this book' : `these ${skippedTotal} books`} — once fixed,
                      they go to the school in a follow-up shipment. Families don't need to do anything.
                    </span>
                  </label>
                ) : (
                  // Nothing shippable: the checkbox would imply confirm can be enabled, but an
                  // empty shipment can't be sent — say so instead of offering a dead control.
                  <p className="pt-2 text-sm text-gray-500">
                    Nothing can ship yet — the shipment can't be sent empty. Fix the books above, then come back.
                  </p>
                )}
              </div>
            )}
            {previewStatus === 'loading' && (
              <p className="text-sm text-gray-500 pb-2">Checking what's ready to ship…</p>
            )}
            {previewStatus === 'error' && (
              <div className="flex items-center justify-between gap-2 mb-2 px-3 py-2 text-sm border border-red-200 bg-red-50 text-red-800 rounded-lg">
                <span>We couldn't load what's ready to ship — sending is paused until we can.</span>
                {onRetryPreview && (
                  <button
                    onClick={onRetryPreview}
                    className="whitespace-nowrap font-bold text-storyblueButton bg-transparent"
                  >
                    Try again
                  </button>
                )}
              </div>
            )}
            <p className="text-xs text-gray-500 pb-1">
              This is a one-time shipment for your class. After you send it, any new purchase ships individually to
              the buyer's own address — families don't need to wait for another release.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row sm:flex-wrap w-full justify-center sm:justify-between items-center pt-3 px-6 sm:px-8 border-t gap-2">
            <BackButton onClick={() => closeModal()}></BackButton>
            <div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">
              <button
                onClick={handleEditAddress}
                className="bg-transparent text-storyblueButton py-2 px-4 rounded-full transition-all text-sm sm:text-base w-full sm:w-auto whitespace-nowrap"
              >
                Edit address
              </button>
              <button
                onClick={() => handleShipSelectedBooks()}
                className={`px-6 py-2 h-9 bg-secondaryStoryAdditionalLightBlueText text-whiteText font-bold leading-none rounded-2xl hover:shadow-md transition-shadow flex items-center justify-center w-full sm:w-auto text-sm sm:text-base whitespace-nowrap ${
                  confirmDisabled ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                disabled={confirmDisabled}
                title={
                  previewStatus === 'error'
                    ? "We couldn't load what's ready to ship — retry the check above first"
                    : previewStatus === 'loading'
                      ? 'Checking what’s ready to ship…'
                      : nothingReady
                        ? 'Nothing can ship yet — the shipment can’t be sent empty'
                        : needsSkipAcknowledgement
                          ? 'Tick the box above to ship without the books that aren’t ready'
                          : undefined
                }
              >
                {shippingLoader
                  ? `Sending ${selectedPurchasedBooks} book${selectedPurchasedBooks === 1 ? '' : 's'}…`
                  : `Send Class Shipment${selectedPurchasedBooks > 0 ? ` (${selectedPurchasedBooks} book${selectedPurchasedBooks === 1 ? '' : 's'})` : ''}`}
                <Loader loader={shippingLoader} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShipToAddressConfirmFlow;
